import { type Micro, Milli } from "@moq/clock";

/** A decoded media frame: codec payload plus its presentation timestamp and keyframe flag. */
export interface Frame {
	/** The codec bitstream payload. */
	payload: Uint8Array;
	/** Presentation timestamp in microseconds. */
	timestamp: Micro;
	/** Whether this frame opens a group, or is a video keyframe. Audio carries it only on a group start. */
	keyframe: boolean;

	/**
	 * How long this frame occupies the presentation timeline. CMAF carries a
	 * per-sample duration; Legacy and LOC report later endpoints through Consumer.next().end.
	 * The consumer adds it to `timestamp` to learn how far a group has presented,
	 * so it can advance to a newer group as soon as the gap is covered instead of
	 * waiting out the max delay budget.
	 */
	duration?: Micro;
}

/** A media frame carrying its local transport ingress time. */
export interface ReceivedFrame extends Frame {
	/** Monotonic ingress time in milliseconds, before reordering or decoder backpressure. */
	arrival: Milli;
}

/** A contiguous span of buffered media, in milliseconds. */
export interface BufferedRange {
	/** Start of the range in milliseconds. */
	start: Milli;
	/** End of the range in milliseconds. */
	end: Milli;
}

/** An ordered list of buffered time ranges. */
export type BufferedRanges = BufferedRange[];

/** Merge two sets of buffered ranges into a single sorted, non-overlapping list. */
export function mergeBufferedRanges(a: BufferedRanges, b: BufferedRanges): BufferedRanges {
	if (a.length === 0) return b;
	if (b.length === 0) return a;

	const result: BufferedRanges = [];
	const all = [...a, ...b].sort((x, y) => x.start - y.start);

	for (const range of all) {
		const last = result.at(-1);
		if (last && last.end >= range.start) {
			last.end = Milli(Math.max(last.end, range.end));
		} else {
			result.push({ ...range });
		}
	}

	return result;
}
